import { useState, useCallback, useEffect } from 'react';
import { useApi } from './useApi';
import { AuthToken, Credentials } from './types';

const TOKEN_STORAGE_KEY = 'ducks_in_rows_auth_token';

export interface UseAuthOptions {
  persistToken?: boolean;
  onAuthChange?: (isAuthenticated: boolean) => void;
}

export function useAuth(options: UseAuthOptions = {}) {
  const { persistToken = true, onAuthChange } = options;
  
  // Initialize token from localStorage immediately
  const getInitialToken = () => {
    if (persistToken && typeof window !== 'undefined') {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    }
    return null;
  };
  
  const initialToken = getInitialToken();
  const [token, setToken] = useState<string | null>(initialToken);
  const [isAuthenticated, setIsAuthenticated] = useState(!!initialToken);
  const [user, setUser] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(!!initialToken);

  const api = useApi({ token: token || undefined });

  // Validate the stored token on mount
  useEffect(() => {
    if (initialToken && persistToken) {
      validateToken(initialToken).then((isValid) => {
        if (!isValid) {
          logout();
        }
        setIsInitializing(false);
      });
    } else {
      setIsInitializing(false);
    }
  }, []); // Only run on mount

  useEffect(() => {
    onAuthChange?.(isAuthenticated);
  }, [isAuthenticated, onAuthChange]);

  const login = useCallback(
    async (credentials: Credentials) => {
      try {
        const response = await api.post<AuthToken>(
          '/api-token-auth/',
          credentials,
          {
            skipAuth: true,
          },
        );
        console.log(response);
        const authToken = response.token;
        setToken(authToken);
        setIsAuthenticated(true);
        setUser(credentials.username);

        if (persistToken) {
          localStorage.setItem(TOKEN_STORAGE_KEY, authToken);
        }

        return authToken;
      } catch (error) {
        setIsAuthenticated(false);
        setToken(null);
        setUser(null);
        throw error;
      }
    },
    [api, persistToken],
  );

  const logout = useCallback(() => {
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);

    if (persistToken) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, [persistToken]);

  const validateToken = useCallback(
    async (testToken?: string) => {
      const tokenToTest = testToken || token;
      if (!tokenToTest) return false;

      try {
        await api.get('/ducks/', {
          headers: {
            Authorization: `Token ${tokenToTest}`,
          },
        });
        return true;
      } catch (error) {
        if (tokenToTest === token) {
          logout();
        }
        return false;
      }
    },
    [api, token, logout],
  );

  const refreshAuth = useCallback(async () => {
    if (token) {
      const isValid = await validateToken(token);
      setIsAuthenticated(isValid);
      return isValid;
    }
    return false;
  }, [token, validateToken]);

  return {
    token,
    isAuthenticated,
    user,
    loading: api.loading || isInitializing,
    error: api.error,
    login,
    logout,
    validateToken,
    refreshAuth,
  };
}
